import './styles.scss'

import { InfoOutlined } from '@mui/icons-material'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme
} from '@mui/material'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import Analytics, {
  EventProperties,
  Events,
  UserProperties
} from 'analytics/Analytics'
import { CheckmarkIcon, CloseIcon } from 'assets/icons'
import Button from 'components/buttons/Button'
import GenericDialog from 'components/dialogs/NoInternetDialog'
import SegmentedControl from 'components/segment-control/SegmentControl'
import { analyticsFeatureToTierFeature } from 'components/tier/OutOfTierDialog'
import { tierToPlan } from 'components/tier/TierHover'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import updateLocale from 'dayjs/plugin/updateLocale'
import { t } from 'i18next'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  changePlanAndUpdateAccount,
  pauseSubscriptionAndUpdateAccount,
  tryPlanAndUpdateAccount,
  updateAccountAfterPlanSubscription
} from 'services/api/payments'
import { Account } from 'services/db/Queries/AccountQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { AccountDoc, AccountProduct, PaddleCheckout } from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import useFitText from 'use-fit-text'
import {
  GROUP_CAL_PROD_PLANIDS,
  hasExhaustedTrialOptions,
  highestActiveProduct,
  highestActiveTier,
  isActivePlanFromAnotherStore,
  P_SKU_BUSINESS_MONTHLY,
  P_SKU_BUSINESS_PLUS_MONTHLY,
  P_SKU_BUSINESS_PLUS_YEARLY,
  P_SKU_BUSINESS_YEARLY,
  P_SKU_PRO_MONTHLY,
  P_SKU_PRO_MONTHLY_NO_TRIAL,
  P_SKU_PRO_YEARLY,
  PADDLE_SKU_BUSINESS_MONTHLY,
  PADDLE_SKU_BUSINESS_PLUS_MONTHLY,
  PADDLE_SKU_BUSINESS_PLUS_YEARLY,
  PADDLE_SKU_BUSINESS_YEARLY,
  PADDLE_SKU_ENTERPRISE_MONTHLY,
  PADDLE_SKU_ENTERPRISE_YEARLY,
  PADDLE_SKU_PRO_MONTHLY,
  PADDLE_SKU_PRO_YEARLY,
  paddlePlanToTier,
  paddleProductToTier,
  Tier,
  trialProduct
} from 'utils/api/accounts'
import { TierFeatures } from 'utils/api/group_state'
import { syncLocalCalendarsToServer } from 'utils/api/sync_local_calendars_to_server/sync_local_calendars_to_server'
import {
  getAdminsCountByPlanKey,
  getPhotoQuotaLimitByPlanKey
} from 'utils/api/tiers'
import { APP_COLOR, hexToRgba } from 'utils/colors'
import { showPurchaseConfetti } from 'utils/confetti'
import { isTestEnv } from 'utils/env'

import ConfirmationDialog from '../ConfirmationDialog'
import { PaddlePrice } from './model'

dayjs.extend(relativeTime)
dayjs.extend(updateLocale)

dayjs.updateLocale('en', {
  relativeTime: {
    future: '%s',
    past: '%s ago',
    s: 'a few seconds',
    m: '1 minute',
    mm: '%d minutes',
    h: '1 hour',
    hh: '%d hours',
    d: '1 day',
    dd: '%d days',
    M: '1 month',
    MM: '%d months',
    y: '1 year',
    yy: '%d years'
  }
})

export type PlanName = '_free_tier' | 'tier1' | 'tier2' | 'tier3' | 'tier4'

interface PlanInfoProps {
  setOpen: (b: boolean) => void
  account?: Model<AccountDoc>
  recommendedPlan: PlanName | null | undefined
  onPurchaseDialogClose?: (
    userAction: 'PURCHASED' | 'DIALOG_CLOSED' | 'PURCHASE_ABORTED',
    plan: PlanName
  ) => void
  open: boolean
  features?: string[] | null
}

type Plan = {
  name: string
  key: PlanName
  description: string
  annualPlanId?: number
  monthlyPlanId?: number
  monthlyPrice: string
  annualPrice: string
}

export const supportedFeatures: Array<{
  title: string
  key: string
  desc: string
  linkText?: string
  linkUrl?: string
  imageUrl?: string
  _free_tier: string | boolean
  tier1: string | boolean
  tier2: string | boolean
  tier3: string | boolean
  tier4: string | boolean
  outOfTierExplanation?: string
  [key: string]: string | boolean | undefined
}> = [
  {
    title: t('numberOfCalendars'),
    key: 'numCalendars',
    _free_tier: `${t('unlimited')}`,
    tier1: `${t('unlimited')}`,
    tier2: `${t('unlimited')}`,
    tier3: `${t('unlimited')}`,
    tier4: `${t('unlimited')}`,
    desc: t('num_calendars_desc')
  },
  {
    title: t('numberOfEvents'),
    key: 'numEvents',
    _free_tier: `${t('unlimited')}`,
    tier1: `${t('unlimited')}`,
    tier2: `${t('unlimited')}`,
    tier3: `${t('unlimited')}`,
    tier4: `${t('unlimited')}`,
    desc: t('num_events_desc')
  },
  {
    title: t('numberOfAdmins'),
    key: 'numAdmins',
    _free_tier: '1',
    tier1: '1',
    tier2: '3',
    tier3: `${t('unlimited')}`,
    tier4: `${t('unlimited')}`,
    desc: t('num_admins_desc'),
    linkText: t('calendarsMembersRoles'),
    linkUrl:
      'https://www.groupcal.app/powerful-permissions-control/#calendar-members-roles',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/calendar_members_roles.jpg?ver=1774857138'
  },
  {
    title: t('eventPhotoAttachments'),
    key: 'eventPhotoAttachments',
    _free_tier: '25',
    tier1: '150',
    tier2: '500',
    tier3: '1500',
    tier4: `${t('unlimited')}`,
    desc: t('eventPhotoAttachmentsTooltip'),
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/Event_photo_attachment.jpg'
  },
  {
    title: t('chooseAddEvents'),
    key: 'permManagementAddEvents',
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('add_events_desc'),
    linkText: t('whoCanAddOrModifyEvents'),
    linkUrl:
      'https://www.groupcal.app/powerful-permissions-control/#control-event-editing',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/permission_add_edit_events.jpg'
  },
  {
    title: t('chooseCalendarNamePhoto'),
    key: 'permManagementCalendarName',
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('change_calendar_desc')
  },
  {
    title: t('chooseCanAddMembers'),
    key: 'permManagementAddMembers',
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('add_members_desc')
  },
  {
    title: t('rsvpForEvents'),
    key: 'rsvp',
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('rsvp_desc')
  },
  {
    title: t('differentColorsPerMembers'),
    key: 'memberColors',
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('color_desc'),
    linkText: t('photosAndColorCoding'),
    linkUrl:
      'https://www.groupcal.app/features-seamless-sharing-collaboration/#member-colors',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/members_color_photos.jpg'
  },
  {
    title: t('hideGroupcalBrand'),
    key: 'removeBranding',
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('hideGroupcalBrandDesc')
  },
  {
    title: t('sharePersonalCalendarTitleFeature'),
    key: 'sharePersonalCalendar',
    _free_tier: false,
    tier1: true,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('purchaseCalendarShareDesc'),
    linkText: t('shareYourPersonalCalendars'),
    linkUrl:
      'https://www.groupcal.app/features-seamless-sharing-collaboration/#share_personal_calendars',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/persoal_accounts_integration.jpg'
  },
  {
    title: t('whoCanSeeEventsDetails'),
    key: 'seeEventsDetails',
    _free_tier: false,
    tier1: false,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('eventVisibilityDesc'),
    outOfTierExplanation: t('eventVisibilityOutOfTier'),
    linkText: t('eventVisibility'),
    linkUrl:
      'https://www.groupcal.app/powerful-permissions-control/#set-event-detail-visibility',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/permission_view_free_busy.jpg'
  },
  {
    title: t('assignEventOwnership'),
    key: 'assignEventOwnership',
    _free_tier: false,
    tier1: false,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('event_ownership_desc'),
    linkText: t('eventOwnership'),
    linkUrl:
      'https://www.groupcal.app/features-seamless-sharing-collaboration/#event-ownership',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/assign_event_ownership.jpg'
  },
  {
    title: t('membersCanEditTheirEventsOnly'),
    key: 'membersCanEditTheirEventsOnly',
    _free_tier: false,
    tier1: false,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('membersCanEditTheirEventsOnlyDesc'),
    linkText: t('editingOnlyOwnEvents'),
    linkUrl:
      'https://www.groupcal.app/powerful-permissions-control/#edit_own_events_only',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/permission_edit_own_events.jpg'
  },
  {
    title: t('prioritySupport'),
    key: 'prioritySupport',
    _free_tier: false,
    tier1: false,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('support_desc')
  },
  {
    title: t('landlineNumber'),
    key: 'landline',
    _free_tier: false,
    tier1: false,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('landline_feature_desc')
  },
  {
    title: t('businessPage'),
    key: 'business',
    _free_tier: false,
    tier1: false,
    tier2: true,
    tier3: true,
    tier4: true,
    desc: t('business_page_desc')
  },
  {
    title: t('calendarChannels'),
    key: 'calendarChannels',
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: true,
    tier4: true,
    desc: t('channels_desc'),
    linkText: t('calendarChannels'),
    linkUrl:
      'https://www.groupcal.app/features-brand-publish-embed/#public-calendar-channels',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/shearchable_public_calendars.jpg'
  },
  {
    title: t('verifiedCalendars'),
    key: 'verifiedChannels',
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: true,
    tier4: true,
    desc: t('verified_desc'),
    linkText: t('calendarVerificationTitle'),
    linkUrl:
      'https://www.groupcal.app/features-brand-publish-embed/#calendar-verification-badge',
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/calendar_verification.jpg'
  },
  {
    title: t('syncEventsAcrossCalendars'),
    key: 'syncEvents',
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: true,
    tier4: true,
    desc: t('sync_desc'),
    linkText: t('masterSchedules'),
    imageUrl:
      'https://www.groupcal.app/wp-content/themes/24me/modules/theme/assets/img/sync_events_across_calendars.jpg',
    linkUrl:
      'https://www.groupcal.app/features-centralized-calendar-management/#sync_events_across_calendars'
  },
  {
    title: t('embedMultipleCalendars'),
    key: 'embedCalendars',
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: true,
    tier4: true,
    desc: t('embedMultipleCalendarsDesc')
  },
  {
    title: t('communityView'),
    key: 'communityView',
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: true,
    tier4: true,
    desc: t('communityViewDesc'),
    linkText: t('embedingCalendars'),
    imageUrl:
      'https://www.groupcal.app/wp-content/uploads/2026/03/Cards-view-on-a-website.jpg',
    linkUrl:
      'https://www.groupcal.app/features-brand-publish-embed/#embed-on-your-website'
  },
  {
    title: t('advancedRestApiTitle'),
    key: 'advancedRestApi',
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: false,
    tier4: true,
    desc: t('advancedRestApiDesc')
  },
  {
    title: t('realTimeWebhooksTitle'),
    key: 'realTimeWebhooks',
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: false,
    tier4: true,
    desc: t('realTimeWebhooksDesc')
  },
  {
    title: t('whiteLabeledWebViewTitle'),
    key: 'whiteLabeledWebView',
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: false,
    tier4: true,
    desc: t('whiteLabeledWebViewDesc')
  },
  {
    title: t('whiteLabeledMobileAppTitle'),
    key: 'whiteLabeledMobileApp',
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: false,
    tier4: true,
    desc: t('whiteLabeledMobileAppDesc')
  },
  {
    title: t('customLegalAndSecurityReviewTitle'),
    key: 'customLegalAndSecurityReview',
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: false,
    tier4: true,
    desc: t('customLegalAndSecurityReviewDesc')
  },
  {
    title: t('dedicatedSuccessManagerTitle'),
    key: 'dedicatedSuccessManager',
    _free_tier: false,
    tier1: false,
    tier2: false,
    tier3: false,
    tier4: true,
    desc: t('dedicatedSuccessManagerDesc')
  }
]

export function logPurchasesAnalytics(
  event: Events,
  account?: AccountDoc,
  additionalData?: {},
  recommendedPlan?: PlanName | null,
  features?: string[] | null
) {
  if (account) {
    const product = highestActiveProduct(account)
    const data = {
      [UserProperties.IS_ACTIVE_SUBS]: product != null ? 'Yes' : 'No',
      [UserProperties.ACTIVE_SUBS]: product?.ID,
      [EventProperties.NEEDED_TIER]:
        recommendedPlanToAnalyticsTier(recommendedPlan),
      [EventProperties.FEATURES_OPENED_WITH]: features?.join(', '),
      [UserProperties.IS_TRIAL_EXPIRED]: hasExhaustedTrialOptions(account)
        ? 'Yes'
        : 'No',
      [UserProperties.IS_IN_ACTIVE_TRIAL]:
        trialProduct(account) != undefined && product?.Status === 'trialing'
          ? 'Yes'
          : 'No',
      [EventProperties.TRIAL_OR_PLAN_EXPIRES_IN]:
        product?.Status === 'trialing' || product?.Status === 'cancelled'
          ? dayjs(Number(product?.ExpiryDate ?? '0') * 1000).diff(
              dayjs(),
              'seconds'
            )
          : undefined,
      ...additionalData
    }
    if (product?.Status !== 'trialing' && product?.Status !== 'cancelled') {
      delete data[EventProperties.TRIAL_OR_PLAN_EXPIRES_IN]
    }
    Analytics.getInstance().sendEvent(event, data)
  }
}

export default function PlanInfo(props: PlanInfoProps) {
  const muiTheme = useTheme()
  const [subscriptionType, setSubscriptionType] = useState<
    'monthly' | 'annually'
  >('monthly')
  const [userSubscriptionType, setUserSubscriptionType] = useState<
    'monthly' | 'annually'
  >('monthly')

  Logger.orange('PlanInfo:HighestProduct', highestActiveProduct(props.account))

  useEffect(() => {
    // set current subscription type based on current user plan
    if (props.account) {
      const activePlan = highestActiveProduct(props.account)
      if (activePlan?.ID?.includes('annually')) {
        setSubscriptionType('annually')
        setUserSubscriptionType('annually')
      } else {
        setSubscriptionType('monthly')
        setUserSubscriptionType('monthly')
      }
    } else {
      setSubscriptionType('monthly')
      setUserSubscriptionType('monthly')
    }
  }, [props.account, props.open])

  const isSameSubscriptionType = subscriptionType === userSubscriptionType

  const segmentControlRef = useRef<HTMLDivElement>(null)
  const monthlySegmentRef = useRef<HTMLDivElement>(null)
  const yearlySegmentRef = useRef<HTMLDivElement>(null)

  const openedForTierFeatures = props.features?.map((feature) =>
    analyticsFeatureToTierFeature(feature)
  )
  Logger.blue('PlansInfo:Features', props.features)
  Logger.blue('PlansInfo:OpenedForTierFeatures', openedForTierFeatures)

  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [infoDialogShowing, setInfoDialogShowing] = useState<boolean>(false)
  const [activeMessage, setActiveMessage] = useState<string>('')

  const {
    setOpen,
    account,
    open,
    recommendedPlan,
    onPurchaseDialogClose,
    features
  } = props
  const highlightAccent =
    muiTheme.palette.mode === 'dark' ? '#ff0032' : APP_COLOR

  useEffect(() => {
    if (open) {
      //log analytics only on opening
      logPurchasesAnalytics(
        Events.OPEN_INAPP_PURCHASE,
        props.account,
        {},
        recommendedPlan,
        features
      )
    }
  }, [open])

  const activePlan: AccountProduct | null | undefined =
    account && highestActiveProduct(account)

  const toggleSubscriptionType = (e: { preventDefault: () => void }) => {
    e.preventDefault()
    if (subscriptionType === 'monthly') {
      setSubscriptionType('annually')
    } else {
      setSubscriptionType('monthly')
    }
  }

  const getPlanFeatures = (planKey: string) => {
    return supportedFeatures.map((feat) => ({
      key: feat.key,
      title: feat.title,
      value: feat[planKey]
    }))
  }

  const syncLocalCalendarsIfNeeded = (acc?: AccountDoc) => {
    // in case of account changes (products) we trigger calendar sync as we might received a product
    if (highestActiveTier(acc) >= Tier.PRO) {
      syncLocalCalendarsToServer()
    }
  }

  const shouldShowPurchaseConfetti = (previousTier: Tier, nextTier: Tier) => {
    return nextTier >= previousTier
  }

  const handleTryForFree = (plan: Plan) => {
    logPurchasesAnalytics(
      Events.CLICK_INAPP_PURCHASE,
      props.account,
      {
        [EventProperties.IS_PURCHASING_TRIAL]: 'Yes'
      },
      recommendedPlan,
      features
    )
    const planIdkey = [plan.key, subscriptionType].join('_')

    const planId = GROUP_CAL_PROD_PLANIDS.find((i) => i.includes(planIdkey))
    if (!planId) {
      throw `${planIdkey} Plan not supported. List ${GROUP_CAL_PROD_PLANIDS}`
    }

    const previousTier = highestActiveTier(account)
    const shouldCelebratePurchase = shouldShowPurchaseConfetti(
      previousTier,
      paddlePlanToTier(plan.key)
    )

    setIsProcessing(true)
    account &&
      tryPlanAndUpdateAccount(account, planId)
        .then((updatedAccount) => {
          logPurchasesAnalytics(
            Events.INAPP_PURCHASED,
            updatedAccount,
            {
              [EventProperties.IS_PURCHASING_TRIAL]: 'Yes'
            },
            recommendedPlan,
            features
          )

          if (shouldCelebratePurchase) {
            showPurchaseConfetti()
          }

          logGTag('Start Trial', {
            send_to: 'AW-10788534611/I-CICPvNnY0YENPyr5go',
            value: '0',
            currency: 'ILS',
            transaction_id: ''
          })

          syncLocalCalendarsIfNeeded(updatedAccount)
          typeof onPurchaseDialogClose === 'function' &&
            updatedAccount &&
            onPurchaseDialogClose('PURCHASED', plan.key)
        })
        .finally(() => setIsProcessing(false))
  }

  const handlePurchase = (plan: Plan) => {
    logPurchasesAnalytics(
      Events.CLICK_INAPP_PURCHASE,
      props.account,
      {
        [EventProperties.IS_PURCHASING_TRIAL]: 'No'
      },
      recommendedPlan,
      features
    )
    const planId =
      subscriptionType === 'monthly' ? plan.monthlyPlanId : plan.annualPlanId

    const previousTier = highestActiveTier(account)
    const shouldCelebratePurchase =
      planId !== undefined &&
      shouldShowPurchaseConfetti(previousTier, paddleProductToTier(planId))

    const price =
      subscriptionType === 'monthly' ? plan.monthlyPrice : plan.annualPrice
    if (planId) {
      window?.Paddle?.Checkout.open({
        product: planId,
        title: t('groupcalPlan', { planName: plan.name }),
        email: account?.PaymentEmail || account?.Email,
        method: 'overlay',
        displayModeTheme: 'light',
        passthrough: JSON.stringify({
          userId: account?._id,
          amplitude_deviceid: Analytics.getInstance().amplitudeDeviceId,
          singularid: Analytics.getInstance().singularDeviceId,
          custom_id: account?._id
        }),
        closeCallback: (e: any) => {
          typeof onPurchaseDialogClose === 'function' &&
            account &&
            onPurchaseDialogClose(
              'PURCHASE_ABORTED',
              activePlan?.ID
                ? subscriptionIdToPlan(activePlan?.ID?.toLowerCase())
                : '_free_tier'
            )
        },
        successCallback: (e: PaddleCheckout) => {
          setIsProcessing(true)
          window?.Paddle?.Order?.details(
            e.checkout.id,
            (data: { state: string; order: { subscription_id: any } }) => {
              if (account) {
                if (data.state === 'processed') {
                  updateAccountAfterPlanSubscription(account, {
                    ...e,
                    checkout: {
                      ...e.checkout,
                      subscriptionId: data.order.subscription_id
                    }
                  })
                    .then((updatedAccount) => {
                      logPurchasesAnalytics(
                        Events.INAPP_PURCHASED,
                        updatedAccount,
                        {
                          [EventProperties.IS_PURCHASING_TRIAL]: 'No'
                        },
                        recommendedPlan,
                        features
                      )

                      if (shouldCelebratePurchase) {
                        showPurchaseConfetti()
                      }

                      logGTag('Purchase', {
                        send_to: 'AW-10788534611/9hGiCP7NnY0YENPyr5go',
                        value: price.slice(1, price.length),
                        currency: price.slice(0, 1),
                        transaction_id: e.checkout.id
                      })

                      syncLocalCalendarsIfNeeded(updatedAccount)

                      typeof onPurchaseDialogClose === 'function' &&
                        updatedAccount &&
                        onPurchaseDialogClose(
                          'PURCHASED',
                          subscriptionIdToPlan(e.product.id)
                        )
                    })
                    .finally(() => setIsProcessing(false))
                }
              }
            },
            false
          )
        }
      })
    }
  }

  const handleCancelPlan = (plan: Plan) => {
    logPurchasesAnalytics(
      Events.INAPP_CANCELLED,
      account,
      {},
      recommendedPlan,
      features
    )

    if (account && isActivePlanFromAnotherStore(account)) {
      setInfoDialogShowing(true)
      setActiveMessage(`${t('useTheAppToCancel')}`)
    } else if (account && activePlan && activePlan.SubscriptionId) {
      setIsProcessing(true)
      pauseSubscriptionAndUpdateAccount(
        account,
        activePlan?.SubscriptionId
      ).finally(() => setIsProcessing(false))
    }
  }

  const handleEnterpriseContact = () => {
    window.open('https://www.groupcal.app/get-a-demo/', '_blank')
  }

  const handleActiveMessageClose = () => {
    setInfoDialogShowing(false)
    setActiveMessage('')
  }

  const handleChangePlan = (plan: Plan) => {
    logPurchasesAnalytics(
      Events.CLICK_INAPP_PURCHASE,
      props.account,
      {
        [EventProperties.IS_PURCHASING_TRIAL]: 'No'
      },
      recommendedPlan,
      features
    )
    if (account && isActivePlanFromAnotherStore(account)) {
      setInfoDialogShowing(true)
      setActiveMessage(
        `${t('yourActivePlanPurchased', { store: activePlan?.Store })}`
      )
    } else if (activePlan?.Status === 'trialing') {
      setInfoDialogShowing(true)
    } else if (
      activePlan &&
      activePlan.SubscriptionId &&
      plan.annualPlanId &&
      plan.monthlyPlanId
    ) {
      const previousTier = highestActiveTier(account)
      const shouldCelebratePurchase = shouldShowPurchaseConfetti(
        previousTier,
        paddlePlanToTier(plan.key)
      )

      setIsProcessing(true)
      account &&
        changePlanAndUpdateAccount(
          account,
          activePlan.SubscriptionId,
          subscriptionType === 'monthly'
            ? plan?.monthlyPlanId
            : plan?.annualPlanId
        )
          .then((updatedAccount) => {
            logPurchasesAnalytics(
              Events.INAPP_PURCHASED,
              updatedAccount,
              {
                [EventProperties.IS_PURCHASING_TRIAL]: 'No'
              },
              recommendedPlan,
              features
            )

            if (shouldCelebratePurchase) {
              showPurchaseConfetti()
            }

            setUserSubscriptionType(subscriptionType)
            account &&
              typeof onPurchaseDialogClose === 'function' &&
              onPurchaseDialogClose(
                'DIALOG_CLOSED',
                plan.annualPlanId
                  ? subscriptionIdToPlan(plan.annualPlanId)
                  : '_free_tier'
              )
          })
          .finally(() => {
            setIsProcessing(false)
          })
    }
  }

  const handleDialogClose = () => {
    setTimeout(() => {
      LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
    }, 250) // timeout to wait close animation
    Account()
      .then((acc) => {
        acc &&
          typeof onPurchaseDialogClose === 'function' &&
          onPurchaseDialogClose(
            'DIALOG_CLOSED',
            tierToPlan(highestActiveTier(acc))
          )
      })
      .finally(() => {
        setOpen(false)
      })
  }

  const PlanNotice = (props: { plan: Plan }) => {
    const { plan } = props
    const isActivePlan = activePlan?.ID?.includes(plan.key)
    const isRecommendedPlan =
      recommendedPlan?.includes(plan.key) &&
      !activePlan?.ID?.includes(recommendedPlan)
    if (isActivePlan) {
      const date = dayjs(Number(activePlan?.ExpiryDate) * 1000).fromNow()
      let notice = t('existingPlan')
      if (activePlan?.Status === 'trialing') notice = t('freeTrialEndsIn')
      if (activePlan?.Status === 'cancelled') notice = t('existingPlanEndsIn')

      return (
        <span
          className={`plan-notice ${plan.key} ${
            activePlan?.Status == 'active' ? 'active' : ''
          }`}
        >
          {notice}
          {(activePlan?.Status === 'trialing' ||
            activePlan?.Status === 'cancelled') && (
            <span className="plan-relative-date">{date}</span>
          )}
        </span>
      )
    }

    if (!activePlan && plan.key === '_free_tier') {
      return (
        <span className={`plan-notice ${plan.key} active`}>{`${t(
          'existingPlan'
        )}`}</span>
      )
    }

    if (isRecommendedPlan) {
      return (
        <span className={`plan-notice ${plan.key} recommended`}>
          {t('requiredPlan')}
        </span>
      )
    }
    return null
  }

  const PlanActionBtn = (props: { plan: Plan }) => {
    const { plan } = props
    let action = null
    let label = ''
    let btnClass = ''

    const canTry = (plan.monthlyPlanId || plan.annualPlanId) && !activePlan

    const isNotActivePlanAndOnTrial =
      (plan.monthlyPlanId || plan.annualPlanId) &&
      activePlan &&
      activePlan.Status === 'trialing' &&
      !activePlan?.ID?.includes(plan.key)

    const canPurchase =
      activePlan &&
      activePlan.Status === 'trialing' &&
      activePlan?.ID?.includes(plan.key)

    const canChangePlan =
      ((plan.monthlyPlanId || plan.annualPlanId) &&
        activePlan &&
        activePlan.Status !== 'trialing' &&
        !activePlan?.ID?.includes(plan.key)) ||
      (activePlan &&
        activePlan.Status !== 'trialing' &&
        activePlan.Status !== 'cancelled' &&
        activePlan?.ID?.includes(plan.key) &&
        !isSameSubscriptionType)

    const canCancel =
      activePlan &&
      activePlan.Status !== 'trialing' &&
      activePlan.Status !== 'cancelled' &&
      activePlan?.ID?.includes(plan.key) &&
      isSameSubscriptionType

    const canActivate =
      activePlan &&
      activePlan.Status !== 'trialing' &&
      activePlan.Status === 'cancelled' &&
      activePlan?.ID?.includes(plan.key)

    const cantTry = hasExhaustedTrialOptions(account)

    if (canTry || isNotActivePlanAndOnTrial) {
      action = handleTryForFree.bind(null, plan)
      label = t('tryForFree')
      btnClass = isNotActivePlanAndOnTrial ? 'changetrialplan-btn' : 'trial-btn'
    }

    if (canPurchase) {
      action = handlePurchase.bind(null, plan)
      label = t('purchase')
      btnClass = 'purchase-btn'
    }

    if (cantTry) {
      action = handlePurchase.bind(null, plan)
      label = t('continue')
      btnClass = 'purchase-btn'
    }

    if (canCancel) {
      action = handleCancelPlan.bind(null, plan)
      label = t('cancelPlan')
      btnClass = 'cancel-plan-btn'
    }

    if (canChangePlan || canActivate) {
      action = handleChangePlan.bind(null, plan)
      const activePlanMeta = plans.find((p) => activePlan?.ID.includes(p?.key))

      function isPlanHigher(plan1: Plan | undefined, plan2: Plan | undefined) {
        if (!plan1 || !plan2) return false
        const tier1 = tierToLevel(plan1.key)
        const tier2 = tierToLevel(plan2.key)
        return tier1 > tier2
      }

      const isHigherPlan = activePlanMeta && isPlanHigher(plan, activePlanMeta)
      if (activePlan.Status === 'cancelled') {
        label = t('activate')
      } else if (isHigherPlan) {
        label = t('upgrade')
      } else if (
        tierToLevel(plan.key) === tierToLevel(activePlanMeta?.key ?? '') &&
        !isSameSubscriptionType
      ) {
        label = t('changePlan')
      } else {
        label = t('downgrade')
      }
      btnClass = 'changeplan-btn'
    }

    if (plan.key === '_free_tier') {
      return null
    }

    if (plan.key === 'tier4') {
      return (
        <Button
          id="purchase-tier4"
          className="trial-btn"
          onClick={handleEnterpriseContact}
          textColor="#fff"
        >
          {t('contactUs')}
        </Button>
      )
    }

    if (!action) {
      return null
    }

    return (
      <Button
        id={`purchase-${plan.key}`}
        className={`trial-btn ${btnClass}`}
        onClick={action?.bind(null)}
        textColor="#fff"
      >
        {label}
      </Button>
    )
  }

  const DialogTitle = () => {
    const hasUsedPlanBefore =
      activePlan?.Status === 'active' ||
      activePlan?.Status === 'cancelled' ||
      hasExhaustedTrialOptions(account)

    if (hasUsedPlanBefore) {
      return (
        <>
          <h2 className="membership-title">
            {t(
              openedForTierFeatures && openedForTierFeatures.length > 0
                ? 'upgradeToUseThisFeature'
                : 'chooseTheRightPlan'
            )}
          </h2>
          <h3 className="membership-subtitle">{t('cancelAnytime')}</h3>
        </>
      )
    }

    return (
      <>
        <h2 className="membership-title">
          {t(
            openedForTierFeatures && openedForTierFeatures.length > 0
              ? 'upgradeToUseThisFeature'
              : 'chooseTheRightPlan'
          )}
        </h2>
        <h3 className="membership-subtitle">{t('try7days')}</h3>
      </>
    )
  }

  const { fontSize, ref } = useFitText({
    minFontSize: 50,
    maxFontSize: 100
  })

  const [isScrolled, setIsScrolled] = useState(false)
  const [, setTierCacheVersion] = useState(0)
  const isScrolledRef = useRef(false)
  const isAnimatingRef = useRef(false)
  const lastScrollTopRef = useRef(0)

  useEffect(() => {
    const handleTierCacheChange = (event: StorageEvent) => {
      if (event.key && event.key !== LocalStorageKey.TIERS_CACHE) return
      setTierCacheVersion((version) => version + 1)
    }

    window.addEventListener('storage', handleTierCacheChange)

    return () => {
      window.removeEventListener('storage', handleTierCacheChange)
    }
  }, [])
  const handleTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget
      const scrollTop = target.scrollTop
      const isAtTop = scrollTop === 0
      const hasScrolled = scrollTop > 0
      lastScrollTopRef.current = scrollTop
      const headerEl = target.querySelector(
        '.plans-header'
      ) as HTMLElement | null

      if (isAnimatingRef.current) return

      // Safety check: if at top, always expand (corrects inconsistent state)
      if (isAtTop && isScrolledRef.current) {
        const beforeHeight = headerEl?.getBoundingClientRect().height ?? 0
        isAnimatingRef.current = true
        isScrolledRef.current = false
        setIsScrolled(false)
        requestAnimationFrame(() => {
          const afterHeight = headerEl?.getBoundingClientRect().height ?? 0
          const delta = afterHeight - beforeHeight
          if (delta > 0) {
            target.scrollTop = Math.max(0, target.scrollTop - delta)
          }
          window.setTimeout(() => {
            isAnimatingRef.current = false
          }, 350)
        })
        return
      }

      // Collapse when scrolling down (scrollTop > 0), but only if currently expanded
      if (!isScrolledRef.current && hasScrolled) {
        const beforeHeight = headerEl?.getBoundingClientRect().height ?? 0
        isAnimatingRef.current = true
        isScrolledRef.current = true
        setIsScrolled(true)
        requestAnimationFrame(() => {
          const afterHeight = headerEl?.getBoundingClientRect().height ?? 0
          const delta = beforeHeight - afterHeight
          if (delta > 0) {
            target.scrollTop += delta
          }
          window.setTimeout(() => {
            isAnimatingRef.current = false
          }, 350)
        })
      }
    },
    []
  )

  const [pricesLoaded, setPricesLoaded] = useState(false)

  const [plans, setPlans] = useState<Array<Plan>>([
    {
      name: t('tierBasic'),
      key: '_free_tier',
      description: t('freeDesc'),
      monthlyPrice: '0',
      annualPrice: '0'
    },
    {
      name: t('tierPro'),
      key: 'tier1',
      annualPlanId: PADDLE_SKU_PRO_YEARLY,
      monthlyPlanId: PADDLE_SKU_PRO_MONTHLY,
      description: t('proDesc'),
      monthlyPrice: '',
      annualPrice: ''
    },
    {
      name: t('tierBusiness'),
      key: 'tier2',
      annualPlanId: PADDLE_SKU_BUSINESS_YEARLY,
      monthlyPlanId: PADDLE_SKU_BUSINESS_MONTHLY,
      description: t('businessDesc'),
      monthlyPrice: '',
      annualPrice: ''
    },
    {
      name: t('tierBusinessPlus'),
      key: 'tier3',
      annualPlanId: PADDLE_SKU_BUSINESS_PLUS_YEARLY,
      monthlyPlanId: PADDLE_SKU_BUSINESS_PLUS_MONTHLY,
      description: t('businessPlusDesc'),
      monthlyPrice: '',
      annualPrice: ''
    },
    {
      name: t('tierEnterprise'),
      key: 'tier4',
      description: t('enterpriseDesc'),
      annualPlanId: PADDLE_SKU_ENTERPRISE_YEARLY,
      monthlyPlanId: PADDLE_SKU_ENTERPRISE_MONTHLY,
      monthlyPrice: '',
      annualPrice: ''
    }
  ])

  const fetchAndUpdatePrices = async () => {
    const updatedPlans = await Promise.all(
      plans.map(async (plan) => {
        try {
          if (plan.annualPlanId) {
            const annualPrice = await fetchPaddlePrice(
              plan.annualPlanId.toString()
            )
            const currencySymbol = annualPrice.price.gross.replace(
              /[\d.,\s]/g,
              ''
            )
            plans[0].annualPrice = `${currencySymbol}0`
            plans[0].monthlyPrice = `${currencySymbol}0`

            const annualPriceNumber = parseFloat(
              annualPrice.price.gross.replace(/[^\d.-]/g, '')
            )
            plan.annualPrice = `${currencySymbol}${(
              annualPriceNumber / 12
            ).toFixed(2)}`
          }
          if (plan.monthlyPlanId) {
            const monthlyPrice = await fetchPaddlePrice(
              plan.monthlyPlanId.toString()
            )
            plan.monthlyPrice = monthlyPrice.price.gross
          }
        } catch (e) {
          setPlans([])
        }
        return plan
      })
    )
    setPlans(updatedPlans)
    setPricesLoaded(true)
  }

  useEffect(() => {
    setPricesLoaded(false)
    fetchAndUpdatePrices()
  }, [open])

  useEffect(() => {
    // because of async paddle, we need to reload prices if user opens this dialog immediately after reload

    setTimeout(() => {
      Logger.orange('PlansDialog:', plans)
      if (
        plans.some(
          (plan) =>
            (plan.monthlyPrice === '' || plan.annualPrice === '') &&
            (plan.monthlyPlanId || plan.annualPlanId)
        )
      ) {
        fetchAndUpdatePrices()
      }
    }, 3000)
  }, [])

  const fetchPaddlePrice = (planId: string): Promise<PaddlePrice> => {
    return new Promise((resolve, reject) => {
      if (
        window.Paddle &&
        window.Paddle.Product &&
        window.Paddle.Product.Prices
      ) {
        window.Paddle.Product.Prices(planId, function (price: PaddlePrice) {
          Logger.red('PaddleProduct', price)
          resolve(price)
        })
      } else {
        Logger.red('Paddle is not defined')
        reject(new Error('Paddle is not defined'))
      }
    })
  }

  const annualSavings = () => {
    if (!pricesLoaded) {
      return null
    }

    const proPlan = plans.find((item) => item.key === 'tier3')
    if (!proPlan) {
      return '-'
    }

    const priceMonthly = proPlan?.monthlyPrice
      .replace(',', '')
      .replace(/^\D+/g, '')
    const priceAnnually = proPlan?.annualPrice
      .replace(',', '')
      .replace(/^\D+/g, '')

    const ratio = parseFloat(priceAnnually) / parseFloat(priceMonthly)

    const savings = 1 - ratio
    return Math.round(savings * 100)
  }

  const subscriptionSegments = [
    {
      label: t('monthly'),
      value: 'monthly',
      ref: monthlySegmentRef,
      id: 'monthly-link'
    },
    {
      label: pricesLoaded
        ? t('yearlySave', { percent: annualSavings() })
        : t('yearly'),
      value: 'annually',
      ref: yearlySegmentRef,
      id: 'yearly-link'
    }
  ]

  const getDisplayedPrice = (plan: Plan) => {
    return pricesLoaded || (plan.monthlyPrice !== '' && plan.annualPrice !== '')
      ? subscriptionType === 'monthly'
        ? plan.monthlyPrice
        : plan.annualPrice
      : '—'
  }

  const featureRows = supportedFeatures.map((feature) => {
    if (feature.key === 'numAdmins') {
      return {
        ...feature,
        _free_tier: String(getAdminsCountByPlanKey('_free_tier') ?? 1),
        tier1: String(getAdminsCountByPlanKey('tier1') ?? 1),
        tier2: String(getAdminsCountByPlanKey('tier2') ?? 3),
        tier3:
          getAdminsCountByPlanKey('tier3') == null
            ? `${t('unlimited')}`
            : String(getAdminsCountByPlanKey('tier3')),
        tier4:
          getAdminsCountByPlanKey('tier4') == null
            ? `${t('unlimited')}`
            : String(getAdminsCountByPlanKey('tier4'))
      }
    }

    if (feature.key === 'eventPhotoAttachments') {
      return {
        ...feature,
        _free_tier: String(getPhotoQuotaLimitByPlanKey('_free_tier') ?? 25),
        tier1: String(getPhotoQuotaLimitByPlanKey('tier1') ?? 150),
        tier2: String(getPhotoQuotaLimitByPlanKey('tier2') ?? 500),
        tier3: String(getPhotoQuotaLimitByPlanKey('tier3') ?? 1500),
        tier4:
          getPhotoQuotaLimitByPlanKey('tier4') == null
            ? `${t('unlimited')}`
            : String(getPhotoQuotaLimitByPlanKey('tier4'))
      }
    }

    return feature
  })

  return (
    <ConfirmationDialog
      title={''}
      fullWidth={true}
      classes={{
        paper: 'plan-container'
      }}
      noPaddingBottom
      noButtons
      scroll="body"
      disableScrollLock
      showCloseIcon
      content={
        <div className="membership-block" id="membership-block">
          <div className="subscription-header">
            <GenericDialog
              title=""
              message={activeMessage}
              leftButton={`${t('ok')}`}
              showing={infoDialogShowing}
              setShowing={handleActiveMessageClose}
            />

            <Backdrop
              sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isProcessing}
            >
              <CircularProgress color="inherit" />
            </Backdrop>

            <DialogTitle />

            <div
              className="subscription-type"
              style={{
                opacity: pricesLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease'
              }}
            >
              <SegmentedControl
                name="subscription-type"
                segments={subscriptionSegments}
                currentIndex={subscriptionType === 'monthly' ? 0 : 1}
                controlRef={segmentControlRef}
                callback={(value: string) => {
                  setSubscriptionType(value as 'monthly' | 'annually')
                }}
              />
            </div>
          </div>

          <div className="membership-table" onScroll={handleTableScroll}>
            <Table
              className="plans-table"
              stickyHeader
              sx={{
                minWidth: 1320,
                width: '100%',
                tableLayout: 'fixed',
                borderCollapse: 'separate',
                borderSpacing: 0,
                marginBottom: '16px'
              }}
            >
              <TableHead
                className={`plans-header ${isScrolled ? 'collapsed' : ''}`}
              >
                <TableRow>
                  <TableCell className="feature-col header-cell" />
                  {plans.map((plan) => (
                    <TableCell
                      key={plan.key}
                      className={`plan-col header-cell ${plan.key}`}
                    >
                      <PlanNotice plan={plan} />
                      <div className="plan-header-content">
                        <h3 className="plan-name">{plan.name}</h3>
                        <div ref={ref} className="plan-description">
                          {plan.description}
                        </div>
                        <div className="plan-price">
                          {plan.key === 'tier4' && (
                            <div className="enterprise-price-label">
                              {t('enterpriseStartingAt')}
                            </div>
                          )}
                          {getDisplayedPrice(plan)}
                          <br />
                          <span>
                            {' '}
                            /{' '}
                            {subscriptionType === 'monthly'
                              ? t('month_one')
                              : t('month_one')}
                          </span>
                        </div>
                        <PlanActionBtn plan={plan} />
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {featureRows.map((feat) => (
                  <TableRow
                    key={feat.key}
                    sx={{
                      backgroundColor: openedForTierFeatures?.includes(
                        feat.key as TierFeatures
                      )
                        ? hexToRgba(highlightAccent, 0.2)
                        : undefined
                    }}
                  >
                    <FeatureTitle
                      feature={feat}
                      highlight={openedForTierFeatures?.includes(
                        feat.key as TierFeatures
                      )}
                    />
                    {plans.map((plan) => {
                      const value = feat[plan.key]
                      return (
                        <TableCell
                          key={plan.key}
                          className={`plan-col ${plan.key}`}
                          align="center"
                        >
                          {typeof value === 'boolean' ? (
                            value ? (
                              <CheckmarkIcon color="#51B87B" />
                            ) : (
                              <CloseIcon color="#FF0A0A" />
                            )
                          ) : (
                            value
                          )}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      }
      open={open}
      rightButtonHidden
      leftButtonHidden
      handleClose={handleDialogClose}
      handleLeftButton={handleDialogClose}
    />
  )
}

interface FeatureTitleProps {
  feature: (typeof supportedFeatures)[number]
  highlight?: boolean
}

function FeatureTitle(props: FeatureTitleProps) {
  const theme = useTheme()
  const scrollRef = useCallback(
    (node: HTMLElement | null) => {
      if (node && props.highlight) {
        setTimeout(() => {
          const scrollContainer = node.closest('.membership-table')
          if (scrollContainer) {
            const headerEl = scrollContainer.querySelector('.plans-header')
            const headerHeight = headerEl?.getBoundingClientRect().height ?? 0
            const containerRect = scrollContainer.getBoundingClientRect()
            const nodeRect = node.getBoundingClientRect()
            const nodeTop =
              nodeRect.top - containerRect.top + scrollContainer.scrollTop
            const nodeCenter = nodeTop + nodeRect.height / 2
            const visibleMiddle =
              headerHeight + (scrollContainer.clientHeight - headerHeight) / 2
            const targetTop = nodeCenter - visibleMiddle

            scrollContainer.scrollTo({
              top: Math.max(0, targetTop),
              behavior: 'smooth'
            })
          }
        }, 300)
      }
    },
    [props.highlight]
  )

  return (
    <TableCell
      className="feature-col"
      ref={scrollRef}
      sx={{
        color: props.highlight ? theme.palette.text.primary : undefined,
        backgroundColor: props.highlight
          ? undefined
          : theme.palette.background.default
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <span>{props.feature.title}</span>
        <GroupcalTooltip
          backgroundColor={theme.palette.background.paper}
          textColor={theme.palette.text.primary}
          textSize="15px"
          delay={0}
          enterNextDelay={0}
          enterDelay={0}
          title={props.feature.desc}
          showArrow
          link={props.feature.linkUrl}
          linkText={props.feature.linkText}
          image={props.feature.imageUrl}
          byPassCypressTest
          tooltipStyles={{
            padding: '16px !important',
            fontWeight: '400 !important',
            borderRadius: '20px !important',
            height: 'fit-content !important'
          }}
        >
          <InfoOutlined
            id={`info-${props.feature.key}`}
            sx={{ fontSize: '1.3rem' }}
          />
        </GroupcalTooltip>
      </Box>
    </TableCell>
  )
}

export function subscriptionIdToPlan(id: number | string): PlanName {
  switch (id) {
    case PADDLE_SKU_PRO_MONTHLY:
    case PADDLE_SKU_PRO_YEARLY:
    case P_SKU_PRO_MONTHLY:
    case P_SKU_PRO_MONTHLY_NO_TRIAL:
    case P_SKU_PRO_YEARLY:
      return 'tier1'
    case PADDLE_SKU_BUSINESS_MONTHLY:
    case PADDLE_SKU_BUSINESS_YEARLY:
    case P_SKU_BUSINESS_MONTHLY:
    case P_SKU_BUSINESS_YEARLY:
      return 'tier2'
    case P_SKU_BUSINESS_PLUS_MONTHLY:
    case P_SKU_BUSINESS_PLUS_YEARLY:
    case PADDLE_SKU_BUSINESS_PLUS_MONTHLY:
    case PADDLE_SKU_BUSINESS_PLUS_YEARLY:
      return 'tier3'
    default:
      return '_free_tier'
  }
}

export function recommendedPlanToAnalyticsTier(
  recommendedPlan: PlanName | undefined | null
): string {
  if (recommendedPlan) {
    recommendedPlan
    switch (recommendedPlan) {
      case 'tier1':
        return 'Tier10'
      case 'tier2':
        return 'Tier20'
      case 'tier3':
        return 'Tier30'
      case 'tier4':
        return 'Tier40'
      default:
        return 'Free'
    }
  }

  return ''
}

export interface GTagProps {
  send_to: string
  value: string
  currency: string
  transaction_id: string
  event_callback?: () => void
}

export function logGTag(type: string, props: GTagProps) {
  if (!isTestEnv()) {
    gtag('event', type, props)
  }
}

function tierToLevel(tier: string): number {
  switch (tier) {
    case '_free_tier':
      return 0
    case 'tier1':
      return 1
    case 'tier2':
      return 2
    case 'tier3':
      return 3
    case 'tier4':
      return 4
    default:
      return 0
  }
}
